import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Client } from "./types";
import { clientApi } from "./apiService";
import { useToast } from "../../../components/Toast";
import { commonButtonProps } from "../../../utils/styles";
import { commonTextFieldProps } from "../../../utils/styles";

const validationSchema = Yup.object().shape({
  clientName: Yup.string().required("Client name is required"),
  primaryContactName: Yup.string().required("Primary contact name is required"),
  primaryContactNumber: Yup.string()
    .required("Primary contact number is required")
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
  email: Yup.string().email("Invalid email format").optional(),
  address: Yup.string().optional(),
  gstNumber: Yup.string().optional(),
});

interface CompanyActionModalProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
  isEdit?: boolean;
  user: any;
  setIsSubmitting: (value: boolean) => void;
  setClients: (clients: Client[]) => void;
  setIsLoading: (value: boolean) => void;
  setShouldFetchClients: (value: boolean) => void;
}

const ClientActionModal = ({
  open,
  onClose,
  client,
  isEdit = false,
  user,
  setIsSubmitting,
  setClients,
  setIsLoading,
  setShouldFetchClients,
}: CompanyActionModalProps) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const { showToast } = useToast();

  const initialValues: Client = {
    ...(client?.id && { id: client?.id }),
    clientName: client?.clientName || "",
    primaryContactName: client?.primaryContactName || "",
    primaryContactNumber: client?.primaryContactNumber || "",
    email: client?.email || "",
    address: client?.address || "",
    gstNumber: client?.gstNumber || "",
    companyId: companyId,
  };

  const handleSubmit = (values: Client) => {
    setIsSubmitting(true);
    if (isEdit) {
      clientApi
        .addEditClient(values, true)
        .then((res) => {
          if (res) {
            setShouldFetchClients(true);
            showToast("Client updated successfully", "success");
          }
        })
        .catch((error) => {
          if (error?.response?.status === 401) {
            showToast("Session expired, please login again", "error");
          } else {
            showToast("Client update failed", "error");
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      clientApi
        .addEditClient(values, false)
        .then((res) => {
          if (res) {
            setShouldFetchClients(true);
            showToast("Client added successfully", "success");
          }
        })
        .catch((error) => {
          if (error?.response?.status === 401) {
            showToast("Session expired, please login again", "error");
          } else {
            showToast("Client add failed", "error");
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Client" : "Add New Client"}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <DialogContent sx={{ p: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Field
                    name="clientName"
                    as={TextField}
                    label="Client Name"
                    fullWidth
                    error={touched.clientName && Boolean(errors.clientName)}
                    helperText={touched.clientName && errors.clientName}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="primaryContactName"
                    as={TextField}
                    label="Primary Contact Name"
                    fullWidth
                    error={
                      touched.primaryContactName &&
                      Boolean(errors.primaryContactName)
                    }
                    helperText={
                      touched.primaryContactName && errors.primaryContactName
                    }
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="primaryContactNumber"
                    as={TextField}
                    label="Primary Contact Number"
                    fullWidth
                    error={
                      touched.primaryContactNumber &&
                      Boolean(errors.primaryContactNumber)
                    }
                    helperText={
                      touched.primaryContactNumber &&
                      errors.primaryContactNumber
                    }
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 10,
                      pattern: "[0-9]*",
                    }}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="gstNumber"
                    as={TextField}
                    label="GST Number (Optional)"
                    fullWidth
                    error={touched.gstNumber && Boolean(errors.gstNumber)}
                    helperText={touched.gstNumber && errors.gstNumber}
                    inputProps={{
                      maxLength: 15,
                      pattern: "[0-9A-Z]*",
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="email"
                    as={TextField}
                    label="Email (Optional)"
                    fullWidth
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="address"
                    as={TextField}
                    label="Address (Optional)"
                    fullWidth
                    multiline
                    rows={2}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} {...commonButtonProps}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                {...commonButtonProps}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ClientActionModal;
